import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

const PublicRoute = ({ children, allowLoggedIn = false }) => {
  const location = useLocation();
  const token = localStorage.getItem('token');
  const userDataString = localStorage.getItem('userData');

  // Define all public routes
  if (location.pathname.startsWith('/admin')) {
    return children;
  }

  const publicRoutes = [
    '/',
    '/login',
    '/signup',
    '/choose-role',
    '/forgot-password',
    '/reset-password'
  ];

  // Routes that are part of onboarding and should be accessible to logged-in users
  const onboardingRoutes = ['/verify-otp', '/verify-login-otp', '/complete-payment', '/tenant/upload-documents'];
  
  const isPublicRoute = publicRoutes.includes(location.pathname);
  const isOnboardingRoute = onboardingRoutes.includes(location.pathname);

  // If allowLoggedIn is true or it's an onboarding route, allow logged-in users
  if (allowLoggedIn || isOnboardingRoute) {
    return children;
  }

  // If it's not a public route, just render children (let protected routes handle their own logic)
  if (!isPublicRoute) {
    return children;
  }

  // Only treat as "logged in" when both token and userData exist (no redirect to dashboard without userData)
  if (!token || !userDataString) {
    return children;
  }

  // If user is on payment page (subscription not completed), don't redirect to dashboard — show public/landing page
  const onPaymentPage = localStorage.getItem('onPaymentPage') === 'true';
  if (onPaymentPage) {
    localStorage.removeItem('onPaymentPage');
    return children;
  }

  // If user is logged in and trying to access a public route, redirect to appropriate dashboard
  try {
    const userData = JSON.parse(userDataString);
    // Extract role from userData (can be in user_details.role or directly in role)
    let role = userData?.user_details?.role || userData?.role || localStorage.getItem('role');
    
    // Convert role to number if it's a string
    if (role !== undefined && role !== null) {
      role = typeof role === 'string' ? parseInt(role, 10) : role;
    }

    // Map role numbers to user types: 1 = landlord, 2 = tenant
    const roleToUserType = {
      1: 'landlord',
      2: 'tenant'
    };

    const currentUserType = roleToUserType[role];

    // Redirect to appropriate dashboard based on user type
    if (currentUserType === 'tenant') {
      return <Navigate to="/tenant/properties" replace />;
    } else if (currentUserType === 'landlord') {
      return <Navigate to="/landlord/dashboard" replace />;
    }
  } catch (error) {
    console.error('Error parsing userData:', error);
    // If there's an error parsing, clear storage and allow access
    localStorage.clear();
    return children;
  }

  // Default: if role doesn't match expected types, allow access
  return children;
};

export default PublicRoute;
